/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import IEnergyManagementProps, {
  ECarbonCartAt
} from '../IEnergyManagementProps';
import { Grid } from '@scuf/common';
import '../EnergyManagementSiteDashboard/EnergyManagementSiteDashboardContent.scss';
import '../EnergyManagementSiteDashboard/EmaasSiteDashboardCommonTable.scss';
import PortfolioEnergyWidget from './PortfolioEnergyWidget/PortfolioEnergyWidget';
import CarbonEmissionKPI from '../CarbonEmissionKPI/CarbonEmissionKPI';
import './EmaasPortfolioCommon.scss';
import UtilityCostWidget from './../UtilityCostWidget/UtilityCostWidget';
import { ContextProvider, updateApiConfig } from '../../../context/ApiContext';
import { shouldComponentNotUpdateOnDateChange } from '../../shared/Utils/Utils';
import { PollingContextProvider } from '../../../context/PollingContext/PollingContext';
import PortfolioDashboardSummaryContent from './PortfolioDashboardSummaryHeader/PortfolioDashboardSummaryContent';
import { UtilityCostType } from '../../shared/Utils/Constants';
import { EnergyUsageDataSourceType } from '../../shared/Enums';
import { withLocalization } from '../../../locales/withLocalization';

const EmaasPortfolioDashboard = React.memo((props: IEnergyManagementProps) => {
  const {
    connectorConfig,
    bearerToken,
    requestContext,
    emaasContext,
    dataSourceInitial,
    storeDataSourceType,
    carbonDataSourceInitial,
    storeCarbonDataSourceInitial
  } = props;

  const [dataSourceType, setDataSourceType] = React.useState<
    EnergyUsageDataSourceType | string | null
  >(
    emaasContext?.enableConsumptionSourceSelect
      ? dataSourceInitial
        ? dataSourceInitial
        : null
      : null
  );

  const [dataSourceTypeCarbon, setDataSourceTypeCarbon] = React.useState<
    EnergyUsageDataSourceType | string | null
  >(
    emaasContext?.cemsEnableCarbonSourceSelect
      ? carbonDataSourceInitial
        ? carbonDataSourceInitial
        : null
      : null
  );
  React.useEffect(() => {
    storeCarbonDataSourceInitial &&
      storeCarbonDataSourceInitial(dataSourceTypeCarbon);
  }, [dataSourceTypeCarbon]);
  return (
    <ContextProvider
      value={updateApiConfig({ connectorConfig, ...emaasContext })}
    >
      <PollingContextProvider>
        <Grid className="layout-grid emaas-portfolio-dashboard">
          <Grid.Row className="layout-portfolio-summary-row">
            <Grid.Column className="emaas-layout-header" width={12}>
              <PortfolioDashboardSummaryContent
                requestContext={requestContext}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="layout-content-grid-row">
            <Grid.Column
              width={4}
              xsWidth={12}
              className="energyusage-kpi-card emaas-siteview-dashboard"
            >
              <CarbonEmissionKPI
                connectorConfig={connectorConfig}
                bearerToken={bearerToken}
                requestContext={requestContext}
                carbonApiFor={ECarbonCartAt.portfolioLabel}
                dataSourceType={dataSourceTypeCarbon}
                setDataSourceType={setDataSourceTypeCarbon}
              />
            </Grid.Column>
            <Grid.Column
              width={4}
              xsWidth={12}
              className="energyusage-kpi-card emaas-siteview-dashboard"
            >
              <PortfolioEnergyWidget
                connectorConfig={connectorConfig}
                requestContext={requestContext}
                bearerToken={bearerToken}
                dataSourceType={dataSourceType}
                setDataSourceType={(type: string | null) => {
                  setDataSourceType(type);
                  storeDataSourceType && storeDataSourceType(type);
                }}
              />
            </Grid.Column>
            <Grid.Column
              width={4}
              xsWidth={12}
              className="energyusage-kpi-card emaas-siteview-dashboard"
            >
              <UtilityCostWidget
                connectorConfig={connectorConfig}
                requestContext={requestContext}
                bearerToken={bearerToken}
                widgetType={UtilityCostType.portfolio}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </PollingContextProvider>
    </ContextProvider>
  );
}, shouldComponentNotUpdateOnDateChange);

export default withLocalization<IEnergyManagementProps>(
  EmaasPortfolioDashboard
);
